import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import ListingCard from '../components/listings/ListingCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { getListings } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { Search, MapPin, SlidersHorizontal, X, Grid, List, ArrowUpDown, Loader2, Sparkles, Flame } from 'lucide-react';

const ListingsPage = () => {
  const { t, language, getTypeLabel } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ 
    city: searchParams.get('city') || '', 
    type: searchParams.get('type') || '',
    types: searchParams.get('types') || '', 
    maxPrice: searchParams.get('maxPrice') || '', 
    minSize: searchParams.get('minSize') || '', 
    furnished: false 
  });

  // Sync URL params with filter state on mount
  useEffect(() => {
    const types = searchParams.get('types');
    if (types) {
      setFilters(prev => ({ ...prev, types }));
    }
  }, []);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');

  const propertyTypes = [
    { id: 'kamer', label: getTypeLabel('kamer') },
    { id: 'appartement', label: getTypeLabel('appartement') },
    { id: 'studio', label: getTypeLabel('studio') },
    { id: 'antikraak', label: getTypeLabel('antikraak') },
  ];

  const priceRanges = [
    { value: 'none', label: t('noMax') },
    { value: '500', label: '€ 500' },
    { value: '750', label: '€ 750' },
    { value: '1000', label: '€ 1.000' },
    { value: '1250', label: '€ 1.250' },
    { value: '1500', label: '€ 1.500' },
    { value: '2000', label: '€ 2.000' },
  ];

  const sizeRanges = [
    { value: 'none', label: t('noMin') },
    { value: '10', label: '10 m²' },
    { value: '15', label: '15 m²' },
    { value: '20', label: '20 m²' },
    { value: '30', label: '30 m²' },
    { value: '50', label: '50 m²' },
  ];

  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const apiFilters = { ...filters };
        if (apiFilters.maxPrice === 'none') apiFilters.maxPrice = '';
        if (apiFilters.minSize === 'none') apiFilters.minSize = '';
        
        const data = await getListings(apiFilters);
        let result = data.listings || [];
        switch (sortBy) {
          case 'price-asc': result.sort((a, b) => a.price - b.price); break;
          case 'price-desc': result.sort((a, b) => b.price - a.price); break;
          case 'size-desc': result.sort((a, b) => b.size - a.size); break;
          default: result.sort((a, b) => new Date(b.postedDate) - new Date(a.postedDate));
        }
        setListings(result);
      } catch { setListings([]); }
      finally { setLoading(false); }
    };
    fetchListings();
  }, [filters, sortBy]);

  const clearFilters = () => { 
    setFilters({ city: '', type: '', types: '', maxPrice: '', minSize: '', furnished: false }); 
    setSearchParams({}); 
  };
  
  const activeFiltersCount = Object.values(filters).filter((v) => v && v !== 'all' && v !== 'none').length;

  const FilterContent = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-900">{t('location')}</label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
          <Input 
            placeholder={t('cityOrNeighborhood')} 
            className="pl-10 h-12 rounded-xl" 
            value={filters.city} 
            onChange={(e) => setFilters({ ...filters, city: e.target.value })} 
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-900">{t('type')}</label>
        <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
          <SelectTrigger className="h-12 rounded-xl">
            <SelectValue placeholder={t('allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allTypes')}</SelectItem>
            {propertyTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-900">{t('maxPrice')}</label>
        <Select value={filters.maxPrice} onValueChange={(v) => setFilters({ ...filters, maxPrice: v })}>
          <SelectTrigger className="h-12 rounded-xl">
            <SelectValue placeholder={t('noMax')} />
          </SelectTrigger>
          <SelectContent>
            {priceRanges.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-900">{t('minSizeFilter')}</label>
        <Select value={filters.minSize} onValueChange={(v) => setFilters({ ...filters, minSize: v })}>
          <SelectTrigger className="h-12 rounded-xl">
            <SelectValue placeholder={t('noMin')} />
          </SelectTrigger>
          <SelectContent>
            {sizeRanges.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center space-x-3 p-4 bg-emerald-50 rounded-xl">
        <Checkbox 
          id="furnished" 
          checked={filters.furnished} 
          onCheckedChange={(c) => setFilters({ ...filters, furnished: c })} 
        />
        <label htmlFor="furnished" className="font-medium text-gray-900">{t('onlyFurnished')}</label>
      </div>
      {activeFiltersCount > 0 && (
        <Button variant="outline" className="w-full h-12 rounded-xl" onClick={clearFilters}>
          <X className="w-4 h-4 mr-2" />{t('clearFilters')}
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-16 md:pt-20">
        {/* Mobile filter */}
        <div className="sticky top-16 md:top-20 z-30 bg-white/95 backdrop-blur-md border-b px-4 py-3 md:hidden">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
              <Input 
                placeholder={t('search')}
                className="pl-10 h-11 rounded-full" 
                value={filters.city} 
                onChange={(e) => setFilters({ ...filters, city: e.target.value })} 
              />
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="h-11 px-3 rounded-full relative">
                  <SlidersHorizontal className="w-5 h-5" />
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
                <SheetHeader className="mb-6">
                  <SheetTitle className="text-xl font-bold text-gray-900">{t('filters')}</SheetTitle>
                </SheetHeader>
                <FilterContent />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-28 bg-white rounded-2xl p-6 shadow-lg border">
                <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-emerald-500" />{t('filters')}
                </h2>
                <FilterContent />
              </div>
            </aside>

            {/* Results */}
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center">
                    {loading ? (language === 'nl' ? 'Laden...' : 'Loading...') : (
                      <>
                        <Flame className="w-7 h-7 mr-2 text-orange-500" />
                        {listings.length} {t('listings')}
                      </>
                    )}
                  </h1>
                  {filters.city && (
                    <p className="text-gray-500 mt-1">
                      {t('in')} {filters.city.charAt(0).toUpperCase() + filters.city.slice(1)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="flex-1 sm:w-[180px] h-11 rounded-full">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">{t('newest')}</SelectItem>
                      <SelectItem value="price-asc">{t('priceAsc')}</SelectItem>
                      <SelectItem value="price-desc">{t('priceDesc')}</SelectItem>
                      <SelectItem value="size-desc">{t('size')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="hidden sm:flex border rounded-full overflow-hidden">
                    <button 
                      className={`p-2.5 ${viewMode === 'grid' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-400'}`} 
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button 
                      className={`p-2.5 ${viewMode === 'list' ? 'bg-emerald-50 text-emerald-600' : 'text-gray-400'}`} 
                      onClick={() => setViewMode('list')}
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
                </div>
              ) : listings.length > 0 ? (
                <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                  {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
                </div>
              ) : (
                <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                  <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('noResults')}</h3>
                  <p className="text-gray-500 mb-6">{t('tryOtherFilters')}</p>
                  <Button onClick={clearFilters} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    {t('clearFilters')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ListingsPage;
